/**
 * Whether a place still operates (`venues.closed_at`, migration
 * 20261002010000).
 *
 * A closed venue keeps its row — old entries and showings point at it — and
 * stops being somewhere to go: the Places index, the map pins and the Bezirk
 * chips leave it out, and its own page says Closed instead of "N coming up".
 * One column, one job: `closed_at` says nothing about the row's events, and
 * nothing here cancels or hides an entry.
 *
 * **Every read is `?? null`.** The `['venues']` query is persisted in
 * IndexedDB, and a cache written before the column existed has rows with no
 * `closed_at` key at all — `undefined`, not `null` — until the query refetches
 * (CLAUDE.md's E5 `people` trap). `undefined` must read as "open".
 *
 * Dependency-free on purpose, so the Worker's suite can test it
 * (`test/venueOpen.test.ts`).
 */
export interface MaybeClosed { closed_at?: string | null }

/** True when the place has been established as no longer operating. Absent
 *  means open. */
export const isClosed = (v: MaybeClosed): boolean => (v.closed_at ?? null) !== null

/** The venues that still operate. Returns the input itself when nothing is
 *  closed, so a memoised derivation keeps a stable identity. */
export function openVenues<T extends MaybeClosed>(venues: readonly T[]): T[] {
  const kept = venues.filter(v => !isClosed(v))
  return kept.length === venues.length ? (venues as T[]) : kept
}

/** The line a closed place's page shows: "Closed", with the month it was
 *  established when the date parses. `closed_at` is a `date` column, so the
 *  value is `YYYY-MM-DD`; anything else prints as a bare "Closed" rather
 *  than "Invalid Date". */
export function closedLine(v: MaybeClosed): string | null {
  const at = v.closed_at ?? null
  if (at === null) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(at)
  if (!m) return 'Closed'
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const when = new Date(Date.UTC(y, mo - 1, d))
  // `Date.UTC` rolls a 13th month into next year rather than refusing it, so
  // the round trip is the check, not `NaN`.
  if (when.getUTCFullYear() !== y || when.getUTCMonth() !== mo - 1 || when.getUTCDate() !== d) return 'Closed'
  const month = when.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  return `Closed · noted ${month}`
}
